import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import {
  useDeleteShift,
  useHoursYear,
  useMarkSubmitted,
  useRules,
  useSaveShift,
  useTimesheets,
} from "../api/hooks";
import type { HoursYear, Shift, TimesheetEntry } from "../api/types";
import { errorText, useFlash } from "../components/Flash";
import { LoadError, Loading, useYear, Stale } from "../components/Layout";
import { TimeSelect } from "../components/TimeSelect";
import { dshort, hm, money, num, timeOf, todayISO, toNum } from "../lib/format";

export function HoursPage() {
  const year = useYear();
  const q = useHoursYear(year);
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  return (
    <Stale on={q.isPlaceholderData}>
      <HoursView data={q.data} />
    </Stale>
  );
}

function HoursView({ data }: { data: HoursYear }) {
  const { year } = data;
  const [editing, setEditing] = useState<number | "new" | null>(null);
  // Shifts grouped by job; the jobs with the latest shifts first.
  const byJob = new Map<number | null, Shift[]>();
  for (const s of data.shifts) byJob.set(s.job_id, [...(byJob.get(s.job_id) ?? []), s]);
  const groups = [...data.jobs].sort((a, b) => b.last.localeCompare(a.last));
  const average = data.days ? data.total_minutes / data.days : 0;

  return (
    <>
      <div className="page-head">
        <h1>Hours, {year}</h1>
        {editing !== "new" && (
          <button type="button" className="btn small quiet" onClick={() => setEditing("new")}>
            Add a shift
          </button>
        )}
      </div>

      {editing === "new" && (
        <div className="panel" style={{ marginBottom: "1.25rem" }}>
          <h2>Add a shift</h2>
          <ShiftForm year={year} onDone={() => setEditing(null)} />
        </div>
      )}

      <TimesheetsPanel year={year} shifts={data.shifts} />

      <div className="two-col">
        <div className="panel">
          <h2>This year</h2>
          <div className="figure">
            <span className="big">{hm(data.total_minutes)}</span>
            <span className="what muted">worked</span>
          </div>
          <dl className="rows">
            <dt>Days with a shift</dt>
            <dd>{data.days}</dd>
            <dt>Average per day</dt>
            <dd>{hm(average)}</dd>
            {data.overtime_after_hours ? (
              <>
                <dt>Overtime (over {num(data.overtime_after_hours)}h a day)</dt>
                <dd>{hm(data.overtime_minutes)}</dd>
              </>
            ) : null}
          </dl>
          {!data.overtime_after_hours && (
            <p className="small muted" style={{ margin: ".6rem 0 0" }}>
              Overtime is off. Set a daily limit in <Link to={`/year/${year}/settings`}>Settings</Link>.
            </p>
          )}
        </div>
        <div className="panel">
          <h2>By job</h2>
          {data.jobs.length ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th className="num">Days</th>
                    <th className="num">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {data.jobs.map((j) => (
                    <tr key={j.job_id ?? "none"}>
                      <td>
                        {j.job_id ? (
                          <Link to={`/job/${j.job_id}`}>{j.name}</Link>
                        ) : (
                          <span className="muted">{j.name}</span>
                        )}
                      </td>
                      <td className="num">{j.days}</td>
                      <td className="num">{hm(j.minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">No hours yet.</p>
          )}
        </div>
      </div>

      {data.overtime_after_hours ? <OvertimePanel data={data} /> : null}

      {!groups.length && (
        <div className="panel" style={{ marginTop: "1.25rem" }}>
          <p>
            No shifts in {year} yet. Use <b>Punch in</b> at the top of the page, or add one above.
          </p>
        </div>
      )}

      {groups.map((g) => (
        <section key={g.job_id ?? "none"} className="panel week" style={{ marginTop: "1.25rem" }}>
          <div className="week-head">
            <div>
              <h2>{g.job_id ? <Link to={`/job/${g.job_id}`}>{g.name}</Link> : g.name}</h2>
              <span className="muted small">
                {g.first === g.last ? dshort(g.first) : `${dshort(g.first)} – ${dshort(g.last)}`}
              </span>
            </div>
            <span className="muted">
              {g.days} {g.days === 1 ? "day" : "days"} · <b>{hm(g.minutes)}</b>
              {g.overtime_minutes > 0 && (
                <>
                  {" "}
                  · {hm(g.overtime_minutes)} overtime
                  {g.unpaid_overtime_minutes === g.overtime_minutes ? (
                    <> (unpaid)</>
                  ) : (
                    g.overtime_pay !== null && <> ({money(g.overtime_pay)})</>
                  )}
                </>
              )}
            </span>
          </div>
          <div className="table-wrap">
            <table className="shifts">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>In</th>
                  <th>Out</th>
                  <th className="num">Break</th>
                  <th className="num">Worked</th>
                  {data.overtime_after_hours ? <th className="num">Overtime</th> : null}
                  <th>Note</th>
                  <th>
                    <span className="visually-hidden">Change</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(byJob.get(g.job_id) ?? []).map((s) =>
                  editing === s.id ? (
                    <tr key={s.id} className="editing">
                      <td colSpan={data.overtime_after_hours ? 8 : 7}>
                        <ShiftForm year={year} shift={s} onDone={() => setEditing(null)} />
                      </td>
                    </tr>
                  ) : (
                    <ShiftRow
                      key={s.id}
                      s={s}
                      overtime={!!data.overtime_after_hours}
                      defaultRate={data.overtime_rate}
                      onEdit={() => setEditing(s.id)}
                    />
                  ),
                )}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}

/** "2026-10-15" -> "15/10/2026", as Employment Hero's form wants it. */
const ehDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const STATUS: Record<TimesheetEntry["status"], [string, string]> = {
  ready: ["ready", "Ready"],
  incomplete: ["pending", "Needs a fix"],
  changed: ["pending", "Changed since sent"],
  submitted: ["active", "Submitted"],
};

const days = (n: number) => (n === 1 ? "1 day" : `${n} days`);

/** Each day's shift as Employment Hero wants it, to copy across, then mark as submitted (several at once). */
function TimesheetsPanel({ year, shifts }: { year: number; shifts: Shift[] }) {
  const q = useTimesheets(year);
  const rules = useRules().data;
  const mark = useMarkSubmitted();
  const flash = useFlash();
  const [showAll, setShowAll] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [fixing, setFixing] = useState<number | null>(null);

  const all = [...(q.data ?? [])].reverse(); // newest first
  if (!all.length) return null;
  const todo = all.filter((e) => e.status !== "submitted");
  const rows = showAll ? all : todo;
  const canPick = (e: TimesheetEntry) => e.status === "ready" || e.status === "changed";
  const pickable = rows.filter(canPick);
  const chosen = pickable.filter((e) => picked.has(e.date)).map((e) => e.date);
  const allChosen = pickable.length > 0 && chosen.length === pickable.length;
  const byId = new Map(shifts.map((s) => [s.id, s]));

  const toggle = (date: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(date)) n.delete(date);
      else n.add(date);
      return n;
    });
  const submit = (dates: string[], submitted: boolean) =>
    mark.mutate(
      { dates, submitted },
      {
        onSuccess: () => {
          setPicked(new Set());
          flash(submitted ? `Marked ${days(dates.length)} as submitted.` : "No longer marked as submitted.");
        },
        onError: (err) => flash(errorText(err), "error"),
      },
    );

  return (
    <section className="panel timesheets" aria-labelledby="ts-h" style={{ marginBottom: "1.25rem" }}>
      <div className="week-head">
        <h2 id="ts-h">Timesheets for Employment Hero</h2>
        <div className="timesheets-head">
          <span className="muted">
            {todo.length ? (
              <>
                <b>{days(todo.length)}</b> to submit
              </>
            ) : (
              "All submitted"
            )}
          </span>
          {rules?.cost_centre && (
            <label className="check small">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show
              Submitted
            </label>
          )}
        </div>
      </div>

      {!rules?.cost_centre ? (
        <p>
          Add your Employment Hero cost centre in <Link to={`/year/${year}/settings`}>Settings</Link> to get
          your timesheets ready.
        </p>
      ) : (
        <>
          <p className="small muted" style={{ margin: "0 0 .6rem" }}>
            Copy each day into Employment Hero&apos;s <i>New timesheet entry</i> (cost centre{" "}
            <b>{rules.cost_centre}</b>), then tick the days you&apos;ve done and mark them as submitted.
          </p>
          {rows.length ? (
            <div className="table-wrap">
              <table className="shifts timesheet-table">
                <thead>
                  <tr>
                    <th className="check-cell">
                      <input
                        type="checkbox"
                        aria-label="Tick every day that's ready"
                        checked={allChosen}
                        disabled={!pickable.length}
                        onChange={() => setPicked(new Set(allChosen ? [] : pickable.map((e) => e.date)))}
                      />
                    </th>
                    <th>Date</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Break</th>
                    <th>Comment</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => {
                    const shift = byId.get(e.shift_id);
                    if (fixing === e.shift_id && shift)
                      return (
                        <tr key={e.date} className="editing">
                          <td colSpan={7}>
                            <ShiftForm year={year} shift={shift} onDone={() => setFixing(null)} />
                          </td>
                        </tr>
                      );
                    const [cls, label] = STATUS[e.status];
                    return (
                      <tr key={e.date} className={e.status === "submitted" ? "is-done" : undefined}>
                        <td className="check-cell">
                          <input
                            type="checkbox"
                            aria-label={`Tick ${dshort(e.date)}`}
                            checked={picked.has(e.date) && canPick(e)}
                            disabled={!canPick(e)}
                            onChange={() => toggle(e.date)}
                          />
                        </td>
                        <td>
                          <b>{ehDate(e.date)}</b>{" "}
                          <span className="muted small">{dshort(e.date).slice(0, 3)}</span>
                        </td>
                        <td>{e.start ?? "–"}</td>
                        <td>
                          {e.end ?? "–"}
                          {e.ends_next_day && (
                            <span className="muted small" title="The next day">
                              {" "}
                              +1
                            </span>
                          )}
                        </td>
                        <td>
                          {e.breaks.length ? e.breaks.map((b) => `${b.start}–${b.end}`).join(", ") : "None"}
                        </td>
                        <td className="note-cell">{e.comment}</td>
                        <td className="status-cell">
                          <span className={`status-pill ${cls}`}>{label}</span>
                          {e.status === "incomplete" && (
                            <>
                              <span className="small"> {e.problems[0]}</span>
                              {shift && (
                                <button
                                  type="button"
                                  className="btn small quiet"
                                  onClick={() => setFixing(e.shift_id)}
                                >
                                  Fix
                                </button>
                              )}
                            </>
                          )}
                          {e.status === "submitted" && (
                            <button
                              type="button"
                              className="btn small quiet"
                              disabled={mark.isPending}
                              onClick={() => submit([e.date], false)}
                            >
                              Undo
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Every day with a shift has been submitted.</p>
          )}
          <div className="actions">
            <button
              type="button"
              className="btn small"
              disabled={!chosen.length || mark.isPending}
              onClick={() => submit(chosen, true)}
            >
              {chosen.length ? `Mark ${days(chosen.length)} as submitted` : "Mark as submitted"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const pct = (rate: number) => `${Math.round(rate * 1000) / 10}%`;
const RATE_NAMES: Record<number, string> = { 1: "straight time", 1.5: "time and a half", 2: "double time" };

/** Overtime hours and what they pay, month by month (overtime is paid monthly). */
function OvertimePanel({ data }: { data: HoursYear }) {
  const months = data.months.filter((m) => m.overtime_minutes > 0);
  const paid = data.hourly_rate !== null;
  const overtimeDays = data.months.reduce((a, m) => a + m.overtime_days, 0);
  return (
    <section className="panel" aria-labelledby="ot-h" style={{ marginTop: "1.25rem" }}>
      <h2 id="ot-h">Overtime</h2>
      <div className="two-col">
        <div>
          {paid ? (
            <div className="pay-figures">
              <div>
                <span className="big">{money(data.overtime_pay)}</span>
                <span className="muted">before tax</span>
              </div>
              <div className="after-tax">
                <span className="big">{money(data.overtime_net)}</span>
                <span className="muted">after tax and NI</span>
              </div>
            </div>
          ) : (
            <div className="figure">
              <span className="big">{hm(data.overtime_minutes)}</span>
              <span className="what muted">overtime</span>
            </div>
          )}
          <dl className="rows">
            <dt>Overtime (over {num(data.overtime_after_hours)}h a day)</dt>
            <dd>{hm(data.overtime_minutes)}</dd>
            {paid && (
              <>
                <dt>Paid for (each day to the nearest ½ hour)</dt>
                <dd>{hm(data.paid_overtime_minutes)}</dd>
              </>
            )}
            <dt>Days with overtime</dt>
            <dd>{overtimeDays}</dd>
            {paid && (
              <>
                <dt>Daily rate (salary ÷ 260)</dt>
                <dd>{money(data.daily_rate)}</dd>
                <dt>Hourly rate (÷ 8)</dt>
                <dd>{money(data.hourly_rate)}</dd>
                <dt>Default overtime rate</dt>
                <dd>
                  ×{num(data.overtime_rate)}
                  {RATE_NAMES[data.overtime_rate] ? ` (${RATE_NAMES[data.overtime_rate]})` : ""}
                </dd>
                <dt>Per overtime hour, by default</dt>
                <dd>{money((data.hourly_rate ?? 0) * data.overtime_rate)}</dd>
                {data.unpaid_overtime_minutes > 0 && (
                  <>
                    <dt>Unpaid (jobs that don&apos;t pay overtime)</dt>
                    <dd>{hm(data.unpaid_overtime_minutes)}</dd>
                  </>
                )}
                <dt className="total">Overtime pay, before tax</dt>
                <dd className="total">{money(data.overtime_pay)}</dd>
                <dt>Income tax</dt>
                <dd>−{money(data.overtime_tax)}</dd>
                <dt>National Insurance</dt>
                <dd>−{money(data.overtime_ni)}</dd>
                <dt className="total">After tax and NI</dt>
                <dd className="total">{money(data.overtime_net)}</dd>
              </>
            )}
          </dl>
          {paid && data.marginal_rates && (
            <p className="small muted" style={{ margin: ".6rem 0 0" }}>
              At your earnings, more overtime is taxed at {pct(data.marginal_rates.tax)} plus{" "}
              {pct(data.marginal_rates.ni)} National Insurance. An estimate using {data.tax_year} rates for
              England, Wales and Northern Ireland; pension, student loan and your tax code aren&apos;t
              included.
            </p>
          )}
          {!paid && (
            <p className="small muted" style={{ margin: ".6rem 0 0" }}>
              Add your salary in <Link to={`/year/${data.year}/settings`}>Settings</Link> to see what your
              overtime pays.
            </p>
          )}
        </div>
        <div>
          {months.length ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="num">Days</th>
                    <th className="num">Overtime</th>
                    {paid && <th className="num">Paid for</th>}
                    {paid && <th className="num">Before tax</th>}
                    {paid && <th className="num">After tax</th>}
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m.month}>
                      <td>{MONTHS[m.month - 1]}</td>
                      <td className="num">{m.overtime_days}</td>
                      <td className="num">{hm(m.overtime_minutes)}</td>
                      {paid && <td className="num">{hm(m.paid_overtime_minutes)}</td>}
                      {paid && <td className="num">{money(m.overtime_pay)}</td>}
                      {paid && <td className="num">{money(m.overtime_net)}</td>}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{data.year}</td>
                    <td className="num">{overtimeDays}</td>
                    <td className="num">{hm(data.overtime_minutes)}</td>
                    {paid && <td className="num">{hm(data.paid_overtime_minutes)}</td>}
                    {paid && <td className="num">{money(data.overtime_pay)}</td>}
                    {paid && <td className="num">{money(data.overtime_net)}</td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="muted">No overtime in {data.year} yet.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function ShiftRow({
  s,
  overtime,
  defaultRate,
  onEdit,
}: {
  s: Shift;
  overtime: boolean;
  defaultRate: number;
  onEdit: () => void;
}) {
  const nextDay = s.end && s.end.slice(0, 10) !== s.start.slice(0, 10);
  return (
    <tr className={s.open ? "is-open" : undefined}>
      <td>{dshort(s.date)}</td>
      <td>{timeOf(s.start)}</td>
      <td>
        {s.end ? (
          <>
            {timeOf(s.end)}
            {nextDay && (
              <span className="muted small" title="The next day">
                {" "}
                +1
              </span>
            )}
          </>
        ) : (
          <span className="badge">punched in</span>
        )}
      </td>
      <td className="num">{s.break_minutes ? `${s.break_minutes}m` : ""}</td>
      <td className="num">
        <b>{hm(s.worked_minutes)}</b>
      </td>
      {overtime && (
        <td className="num">
          {s.overtime_minutes ? hm(s.overtime_minutes) : ""}
          {s.overtime_paid && s.overtime_minutes > 0 && s.paid_overtime_minutes !== s.overtime_minutes && (
            <span className="muted" title="Paid to the nearest half hour">
              {" "}
              → {hm(s.paid_overtime_minutes)}
            </span>
          )}
          {s.overtime_minutes > 0 && !s.overtime_paid && <span className="pill">unpaid</span>}
          {s.overtime_minutes > 0 && s.overtime_paid && s.overtime_rate !== defaultRate && (
            <span className="pill">×{num(s.overtime_rate)}</span>
          )}
        </td>
      )}
      <td className="note-cell">{s.note}</td>
      <td>
        <button type="button" className="btn small quiet" onClick={onEdit}>
          Change
        </button>
      </td>
    </tr>
  );
}

function ShiftForm({ year, shift, onDone }: { year: number; shift?: Shift; onDone: () => void }) {
  const save = useSaveShift();
  const del = useDeleteShift();
  const flash = useFlash();
  const today = todayISO();
  const [f, setF] = useState({
    date: shift?.date ?? (today.startsWith(String(year)) ? today : `${year}-01-01`),
    start: shift ? timeOf(shift.start) : "",
    end: shift?.end ? timeOf(shift.end) : "",
    untimed: shift?.untimed_break_minutes ? String(shift.untimed_break_minutes) : "",
    note: shift?.note ?? "",
  });
  // The breaks with times (one still going while punched in is kept as it is, so it isn't listed).
  const [breaks, setBreaks] = useState(
    (shift?.breaks ?? []).filter((b) => b.end).map((b) => ({ start: timeOf(b.start), end: timeOf(b.end!) })),
  );
  const hadUntimed = !!shift?.untimed_break_minutes;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const setBreak = (i: number, k: "start" | "end") => (v: string) =>
    setBreaks((bs) => bs.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const overnight = f.start && f.end && f.end <= f.start;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const given = breaks.filter((b) => b.start || b.end);
    if (given.some((b) => !b.start || !b.end)) return flash("Give each break a start and an end.", "error");
    save.mutate(
      {
        id: shift?.id ?? null,
        shift: {
          date: f.date,
          start: f.start,
          end: f.end || null,
          break_minutes: Math.round(toNum(f.untimed)),
          breaks: given,
          note: f.note.trim() || null,
        },
      },
      {
        onSuccess: () => {
          flash(shift ? "Shift saved." : "Shift added.");
          onDone();
        },
        onError: (err) => flash(errorText(err), "error"),
      },
    );
  };

  const remove = () => {
    if (!shift || !window.confirm(`Delete the shift on ${dshort(shift.date)}?`)) return;
    del.mutate(shift.id, {
      onSuccess: () => {
        flash("Shift deleted.");
        onDone();
      },
      onError: (err) => flash(errorText(err), "error"),
    });
  };

  const id = (k: string) => `shift-${shift?.id ?? "new"}-${k}`;
  return (
    <form onSubmit={submit} className="shift-form">
      <div className="fields">
        <div>
          <label htmlFor={id("date")}>Day</label>
          <input id={id("date")} type="date" required value={f.date} onChange={set("date")} />
        </div>
        <div>
          <label htmlFor={id("start")}>In</label>
          <TimeSelect
            id={id("start")}
            required
            value={f.start}
            onChange={(v) => setF((x) => ({ ...x, start: v }))}
          />
        </div>
        <div>
          <label htmlFor={id("end")}>Out {overnight && <span className="muted">(next day)</span>}</label>
          <TimeSelect id={id("end")} value={f.end} onChange={(v) => setF((x) => ({ ...x, end: v }))} />
        </div>
        <div className="wide">
          <label htmlFor={id("note")}>Note</label>
          <input id={id("note")} maxLength={500} value={f.note} onChange={set("note")} />
        </div>
      </div>
      <fieldset className="breaks-edit">
        <legend>Breaks</legend>
        {breaks.map((b, i) => (
          <div className="break-row" key={i}>
            <span className="time-pair">
              <TimeSelect id={id(`break-${i}-start`)} value={b.start} onChange={setBreak(i, "start")} />
              <span aria-hidden="true">to</span>
              <TimeSelect id={id(`break-${i}-end`)} value={b.end} onChange={setBreak(i, "end")} />
            </span>
            <label className="visually-hidden" htmlFor={id(`break-${i}-start`)}>
              Break {i + 1} start
            </label>
            <label className="visually-hidden" htmlFor={id(`break-${i}-end`)}>
              Break {i + 1} end
            </label>
            <button
              type="button"
              className="btn small quiet"
              onClick={() => setBreaks((bs) => bs.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn small quiet"
          onClick={() => setBreaks((bs) => [...bs, { start: "", end: "" }])}
        >
          Add a break
        </button>
        {(hadUntimed || toNum(f.untimed) > 0) && (
          <div className="untimed">
            <label htmlFor={id("untimed")}>Break without times (minutes)</label>
            <input
              id={id("untimed")}
              type="number"
              min="0"
              step="5"
              placeholder="0"
              value={f.untimed}
              onChange={set("untimed")}
            />
            <span className="small muted">
              Employment Hero needs break times: add this as a break above, then set this to 0.
            </span>
          </div>
        )}
      </fieldset>
      <p className="small muted" style={{ margin: ".5rem 0 0" }}>
        An Out time earlier than In is the next morning. Leave Out empty if you&apos;re still punched in.
      </p>
      <div className="actions">
        <button className="btn small" type="submit" disabled={save.isPending}>
          {shift ? "Save" : "Add shift"}
        </button>
        <button className="btn small quiet" type="button" onClick={onDone}>
          Cancel
        </button>
        {shift && (
          <button className="btn small danger" type="button" onClick={remove} disabled={del.isPending}>
            Delete
          </button>
        )}
      </div>
    </form>
  );
}
